class ProductZoomModal extends HTMLElement {
  constructor() {
    super();
    
    this.selectors = {
      slideshow: '[data-product-zoom-slideshow]',
      slides: '[data-product-zoom-slide]',
      pagination: '[data-product-zoom-pagination]',
      thumbnails: '[data-product-zoom-thumb]',
      close: '[data-product-zoom-close]',
      modal: '[data-product-zoom-modal]',
      zoomTrigger: '[data-zoom-image]',
      zoomImageInner: '[data-zoom-image-inner]'
    };
    
    this.mediaQueries = {
      largeUp: window.matchMedia('(min-width: 1024px)')
    };
    
    this.init();
  }

  init() {
    // Only initialize on desktop
    this.mediaQueries.largeUp.addEventListener('change', this.setup.bind(this));
    if (this.mediaQueries.largeUp.matches) {
      this.setup(this.mediaQueries.largeUp);
    }
  }

  isHandledByMobileZoom(target) {
    const gallery = target.closest(
      'media-gallery[data-mobile-pinch-zoom="true"], media-gallery[data-quick-add-zoom]'
    );
    if (!gallery) return false;

    const isQuickAdd =
      !!target.closest('quick-add-modal') ||
      !!target.closest('quick-add-modal-mobile');
    if (isQuickAdd) {
      const style = gallery.dataset.quickAddZoom || 'overlay';
      return style === 'overlay' || style === 'inline';
    }

    return !this.mediaQueries.largeUp.matches && gallery.dataset.mobilePinchZoom;
  }

  setup(mediaQuery) {
    if (!mediaQuery.matches) return;
    
    // Setup close button
    this.querySelector(this.selectors.close)?.addEventListener('click', () => {
      this.close();
    });
    
    // Setup escape key
    document.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && this.classList.contains('is-visible')) {
        this.close();
      }
    });
    
    // Setup click handlers for zoom triggers
    document.querySelectorAll(this.selectors.zoomTrigger).forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (this.isHandledByMobileZoom(event.target)) return;

        const slideElement = event.target.closest('.swiper-slide, .product-media-gallery__grid-item');
        if (slideElement) {
          const slideIndex = Array.from(slideElement.parentNode.children).indexOf(slideElement);
          this.open(slideIndex);
        }
      });
    });
  }

  open(slideIndex = 0) {
    // Load panzoom library if not already loaded
    if (!window.Panzoom) {
      const script = document.createElement('script');
      script.src = this.dataset.panzoomUrl;
      document.body.appendChild(script);
      script.onload = () => this.openModal(slideIndex);
    } else {
      this.openModal(slideIndex);
    }
  }

  openModal(slideIndex) {
    document.body.classList.add('overflow-hidden');
    this.classList.remove('is-hidden');
    this.classList.add('is-visible');
    
    // Load images lazily
    this.loadImages();
    
    // Setup thumbnails and slideshow
    this.setupSlideshow();
    
    // Go to the clicked image
    this.goToSlide(slideIndex, 'auto');
    
    // Trap focus for accessibility
    trapFocus(this, this.querySelector(this.selectors.close));
  }

  close() {
    document.body.classList.remove('overflow-hidden');
    this.classList.remove('is-visible');
    this.classList.add('is-hidden');
    
    // Clean up zoom
    this.closeZoom();
    
    // Remove focus trap
    removeTrapFocus();
  }

  loadImages() {
    // Load slide images
    this.querySelectorAll(this.selectors.slides).forEach((slide, index) => {
      if (!slide.querySelector('img')) {
        const img = document.createElement('img');
        img.src = slide.dataset.imgUrl;
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.alt = slide.dataset.alt || '';
        img.addEventListener('click', () => this.zoomImage(img));
        
        const container = document.createElement('div');
        container.className = 'product-zoom-modal__image-container';
        container.appendChild(img);
        slide.appendChild(container);
      }
    });
    
    // Load thumbnail images
    this.querySelectorAll(this.selectors.thumbnails).forEach((thumb, index) => {
      if (!thumb.querySelector('img')) {
        const img = document.createElement('img');
        img.src = thumb.dataset.imgUrl;
        img.loading = 'lazy';
        img.alt = `Thumbnail ${index + 1}`;
        thumb.appendChild(img);
      }
    });
  }

  setupSlideshow() {
    const slideshow = this.querySelector(this.selectors.slideshow);
    const thumbnails = this.querySelectorAll(this.selectors.thumbnails);
    
    // Setup thumbnail click handlers
    thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const slideIndex = parseInt(thumb.dataset.index);
        this.goToSlide(slideIndex);
      });
    });
    
    // Setup intersection observer for active thumbnail
    const observerOptions = {
      root: slideshow,
      rootMargin: '0px',
      threshold: 0.5
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slideIndex = parseInt(entry.target.dataset.index);
          this.setActiveThumbnail(slideIndex);
        }
      });
    }, observerOptions);
    
    this.querySelectorAll(this.selectors.slides).forEach((slide) => {
      observer.observe(slide);
    });
    
    this.observer = observer;
  }

  goToSlide(index, behavior = 'smooth') {
    this.closeZoom();
    const slide = this.querySelector(`${this.selectors.slides}[data-index="${index}"]`);
    if (slide) {
      slide.scrollIntoView({ behavior, block: 'nearest' });
      this.setActiveThumbnail(index);
    }
  }

  setActiveThumbnail(index) {
    this.querySelectorAll(this.selectors.thumbnails).forEach((thumb) => {
      thumb.classList.toggle('is-active', parseInt(thumb.dataset.index) === index);
    });
  }

  zoomImage(imgElement) {
    if (this.panzoomInstance) {
      this.closeZoom();
      return;
    }
    
    // Create zoom overlay
    const zoomOverlay = document.createElement('div');
    zoomOverlay.className = 'product-zoom-modal__zoom-overlay';
    
    const zoomImg = document.createElement('img');
    zoomImg.src = imgElement.src;
    zoomImg.alt = imgElement.alt;
    zoomImg.dataset.zoomImageInner = '';
    
    zoomOverlay.appendChild(zoomImg);
    this.appendChild(zoomOverlay);
    
    // Initialize Panzoom
    this.panzoomInstance = Panzoom(zoomImg, {
      maxScale: 2.5,
      minScale: 1,
      handleStartEvent: (event) => {
        event.preventDefault();
        event.stopPropagation();
      }
    });
    
    // Auto zoom
    this.panzoomInstance.zoom(1.5, { animate: true });
    
    // Enable wheel zoom
    zoomImg.parentElement.addEventListener('wheel', this.panzoomInstance.zoomWithWheel);
    
    // Track if user is dragging
    let isDragging = false;
    zoomImg.addEventListener('panzoomstart', () => {
      isDragging = false;
    });
    
    zoomImg.addEventListener('panzoomchange', () => {
      isDragging = true;
    });
    
    zoomImg.addEventListener('panzoomend', () => {
      if (!isDragging) {
        this.closeZoom();
      }
    });
    
    this.zoomOverlay = zoomOverlay;
  }

  closeZoom() {
    if (this.panzoomInstance) {
      this.panzoomInstance.destroy();
      this.panzoomInstance = null;
    }
    
    if (this.zoomOverlay) {
      this.zoomOverlay.remove();
      this.zoomOverlay = null;
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.closeZoom();
  }
}

customElements.define('product-zoom-modal', ProductZoomModal);

